/**
 * Builds the ProjectContext that resolvers consume. Pure transform from
 * the actual stored entities to the shape resolvers depend on. Hoisted
 * here so the master-spec editor preview, the compile pipeline, and the
 * Word export path can all build the same shape consistently.
 */

import type { ContentBlock, Issuance, ProjectContext, ProjectSpec } from '../../shared/types';

export interface ContextInputs {
  project: {
    id: string;
    name?: string;
    number?: string;
    address?: string;
    phase?: string;
    [k: string]: any;
  };
  thisSpec: {
    id: string;
    masterId?: string;
    csiNumber?: string;
    sectionNumber?: string;
    title?: string;
    division?: string;
    coreContent: ContentBlock[];
  };
  allProjectSpecs: ProjectSpec[];
  team: Array<{
    id: string;
    name?: string;
    email?: string;
    role?: string;
    title?: string;
  }>;
  client?: { id: string; name?: string; address?: string };
  currentIssuance?: Issuance;
  resolvedAt?: string;
}

/** The fabricated project `buildSampleContext` uses — resolvers that have
 *  nothing honest to show without a real project check for it. */
export const SAMPLE_PROJECT_ID = 'sample';

export function buildProjectContext(inp: ContextInputs): ProjectContext {
  const others = inp.allProjectSpecs
    .filter((s) => s.id !== inp.thisSpec.id)
    .map((s) => ({
      id: s.id,
      sectionNumber: s.csiNumber,
      csiNumber: s.csiNumber,
      title: s.title,
      division: (s.csiNumber ?? '').split(' ')[0] || undefined,
    }));
  return {
    project: inp.project,
    thisSpec: {
      ...inp.thisSpec,
      division:
        inp.thisSpec.division ??
        (inp.thisSpec.csiNumber ?? inp.thisSpec.sectionNumber ?? '').split(' ')[0],
    },
    otherSpecsInProject: others,
    archivedSpecIds: inp.allProjectSpecs.filter((s) => !!s.archivedAt).map((s) => s.id),
    team: inp.team,
    client: inp.client,
    currentIssuance: inp.currentIssuance,
    resolvedAt: inp.resolvedAt ?? new Date().toISOString(),
  };
}

/**
 * Builds a synthetic ProjectContext for the master-spec editor preview.
 * Master specs don't have a project — so we fabricate one with placeholder
 * fields so authors can see roughly what the smart block will produce when
 * compiled into a real project.
 */
export function buildSampleContext(thisSpec: {
  id: string;
  csiNumber?: string;
  title?: string;
  coreContent: ContentBlock[];
}): ProjectContext {
  return buildProjectContext({
    project: {
      id: SAMPLE_PROJECT_ID,
      name: '[Project Name]',
      number: '[Project Number]',
      address: '[Project Address]',
      phase: '[Phase]',
    },
    thisSpec: {
      id: thisSpec.id,
      csiNumber: thisSpec.csiNumber,
      sectionNumber: thisSpec.csiNumber,
      title: thisSpec.title ?? '[Section Title]',
      coreContent: thisSpec.coreContent,
    },
    allProjectSpecs: [],
    team: [
      {
        id: 'sample-eng',
        name: '[Engineer Name]',
        email: '[engineer@firm.com]',
        title: 'Project Engineer',
        role: 'engineer',
      },
    ],
    client: { id: 'sample', name: '[Client Name]', address: '[Client Address]' },
    resolvedAt: new Date().toISOString(),
  });
}
